/**
 * Attachment Service
 * Handles entity file attachment API calls
 */
import axios from 'axios';
import BaseApiService from '../base/BaseApiService';
import config from '@config';
import authService from '../authServiceFactory';
import type {
  AttachmentEntityType,
  AttachmentListResponse,
  AttachmentUploadResponse,
  AttachmentDownloadResponse,
  AttachmentDeleteResponse,
} from '@app-types/attachment';

class AttachmentService extends BaseApiService {
  async listAttachments(
    entityType: AttachmentEntityType,
    entityId: number,
  ): Promise<AttachmentListResponse> {
    return this.get<AttachmentListResponse>(`/attachments/${entityType}/${entityId}`);
  }

  async uploadAttachments(
    entityType: AttachmentEntityType,
    entityId: number,
    files: File[],
  ): Promise<AttachmentUploadResponse> {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file);
    });

    const service = await authService;
    const token = await service.getAccessToken();

    const response = await axios.post<AttachmentUploadResponse>(
      `${config.apiBaseUrl}/attachments/${entityType}/${entityId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data;
  }

  async getDownloadUrl(
    entityType: AttachmentEntityType,
    entityId: number,
    attachmentId: number,
  ): Promise<string> {
    const response = await this.get<AttachmentDownloadResponse>(
      `/attachments/${entityType}/${entityId}/${attachmentId}/download`,
    );
    return response.downloadUrl;
  }

  async deleteAttachment(
    entityType: AttachmentEntityType,
    entityId: number,
    attachmentId: number,
  ): Promise<AttachmentDeleteResponse> {
    return this.delete<AttachmentDeleteResponse>(
      `/attachments/${entityType}/${entityId}/${attachmentId}`,
    );
  }
}

export const attachmentService = new AttachmentService();
